import { devDebug } from "@/lib/logger";
import axios from "axios";
import { getSessionId, getSource, getJourneyId } from "@/lib/quizStorage";
import { requestChallengeResolution } from "@/lib/antibot";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/*
 * Défi anti bot adaptatif (sécurité, aout 2026).
 * Quand le backend détecte un comportement anormal (limite de débit
 * dépassée), il renvoie un 429 accompagné d'un petit défi signé. Cet
 * interceptor global affiche alors la modale de vérification puis rejoue
 * automatiquement la requête d'origine une seule fois.
 * Garde fous : jamais de rejeu multiple (_challengeRetried), jamais de
 * modale pour les appels marqués _skipChallenge (analytics, vérification
 * du défi lui même).
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const data = error.response ? error.response.data : null;
    const shouldChallenge =
      error.response &&
      error.response.status === 429 &&
      data &&
      data.challenge &&
      !config._challengeRetried &&
      !config._skipChallenge;
    if (shouldChallenge) {
      try {
        await requestChallengeResolution(data.challenge);
      } catch (challengeError) {
        // Défi abandonné ou indisponible : l'erreur d'origine est propagée.
        return Promise.reject(error);
      }
      config._challengeRetried = true;
      return axios(config);
    }
    return Promise.reject(error);
  },
);

function deviceType() {
  try {
    return window.innerWidth < 768 ? "mobile" : "desktop";
  } catch (error) {
    // Environnement sans fenetre (tests) : valeur neutre.
    return "unknown";
  }
}

/*
 * Mesure du tunnel : événements anonymes uniquement.
 * Champs transmis : nom de l'événement, étape, type d'appareil, source
 * commerciale, identifiant anonyme de session. JAMAIS de contenu de santé.
 */
export async function trackEvent(eventType, step) {
  try {
    await axios.post(`${API}/events`, {
      session_id: getSessionId(),
      event_type: eventType,
      step: step || null,
      device_type: deviceType(),
      source: getSource() || null,
    }, { _skipChallenge: true });
  } catch (error) {
    // Les analytics ne doivent jamais bloquer l'experience.
    // Journalisation controlee : type d'evenement uniquement, aucun contenu.
    devDebug(`[cycliq] evenement non transmis (${eventType})`);
  }
}

export async function fetchQuizDefinition() {
  const res = await axios.get(`${API}/quiz/definition`);
  return res.data;
}

/*
 * CYCLIQ FIT : envoi de la qualification commerciale (liste blanche stricte,
 * validée aussi côté serveur). Jamais de réponses du diagnostic, de scores
 * santé ni de safety_flag. Jamais bloquant pour l'expérience.
 */
export async function syncFit(payload) {
  try {
    await axios.post(`${API}/fit/sync`, payload);
  } catch (error) {
    devDebug("[cycliq] synchronisation FIT non transmise");
  }
}

export async function fetchPublicSettings() {
  const res = await axios.get(`${API}/settings/public`);
  return res.data;
}

/*
 * État public du service : disponibilité, base de données et pause en cours.
 * Aucune donnée sensible. L'appel ignore le défi anti bot : la page statut
 * doit rester lisible en toutes circonstances.
 */
export async function fetchServiceStatus() {
  const res = await axios.get(`${API}/status`, { _skipChallenge: true });
  return res.data;
}

/*
 * Création du parcours : preuve minimale du consentement sensible.
 * Contenu : identifiant commercial aléatoire, version du texte, date côté
 * serveur. AUCUNE réponse, AUCUN score, AUCUNE donnée de santé.
 */
export async function createJourney(consentVersion) {
  const res = await axios.post(`${API}/journeys`, {
    journey_id: getJourneyId(),
    consent_version: consentVersion || "",
  });
  return res.data;
}

/*
 * Envoi du bilan par email : le PDF est DÉJÀ chiffré (AES 256) dans le
 * navigateur avant cet appel. Le serveur ne reçoit jamais le code personnel
 * ni aucune donnée de santé en clair.
 */
export async function sendBilanEmail({ prenom, email, telephone, consentService, consentMarketing, consentPhone, consentVersion, pdfBase64 }) {
  const res = await axios.post(`${API}/bilan/send`, {
    journey_id: getJourneyId(),
    prenom: prenom || "",
    email,
    telephone: telephone || null,
    consent_service: Boolean(consentService),
    consent_marketing: Boolean(consentMarketing),
    consent_phone: Boolean(consentPhone),
    consent_version: consentVersion || "",
    pdf_base64: pdfBase64,
  }, { timeout: 60000 });
  return res.data;
}

/* ----- Administration ----- */

export async function adminGetSettings(adminKey) {
  const res = await axios.get(`${API}/admin/settings`, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

export async function adminUpdateSettings(adminKey, updates) {
  const res = await axios.put(`${API}/admin/settings`, updates, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/*
 * Statistiques agrégées du tunnel : compteurs anonymes d'événements,
 * total de parcours, bilans envoyés, relances et rendez vous.
 * Périodes : all, 7d, 30d. Aucune donnée de santé.
 */
export async function adminGetFunnel(adminKey, period = "all") {
  const res = await axios.get(`${API}/admin/funnel`, {
    headers: { "X-Admin-Key": adminKey },
    params: { period },
  });
  return res.data;
}

/*
 * Désinscription des emails de rappel : jeton unique reçu dans l'email.
 * Aucune autre donnée n'est transmise.
 */
export async function unsubscribeRelance(token) {
  const res = await axios.post(`${API}/relance/unsubscribe`, { token });
  return res.data;
}

/*
 * Aperçu HTML d'un email de rappel avec des données d'exemple. Aucun envoi.
 */
export async function adminPreviewRelance(adminKey, subject, body) {
  const res = await axios.post(
    `${API}/admin/relances/preview`,
    { subject, body },
    { headers: { "X-Admin-Key": adminKey } },
  );
  return res.data;
}

/*
 * Envoi immédiat d'un rapport hebdomadaire de test au destinataire configuré.
 */
export async function adminRunWeeklyReport(adminKey) {
  const res = await axios.post(`${API}/admin/weekly-report/run`, {}, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/*
 * Progression de l'objectif de cohorte : ventes GoHighLevel, bilans et
 * rendez vous de la tranche en cours. Compteurs agrégés uniquement.
 */
export async function adminGetObjective(adminKey) {
  const res = await axios.get(`${API}/admin/objective`, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/*
 * Compteurs de sécurité : défis anti bot déclenchés et réussis sur 7 jours,
 * avec historique jour par jour et seuil d'alerte email.
 * Décomptes agrégés uniquement, aucune donnée personnelle.
 */
export async function adminGetSecurity(adminKey) {
  const res = await axios.get(`${API}/admin/security`, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/*
 * Envoi immédiat d'une alerte sécurité de test vers le destinataire du
 * rapport hebdomadaire (ignore le seuil et la limite quotidienne).
 */
export async function adminTestSecurityAlert(adminKey) {
  const res = await axios.post(`${API}/admin/security/alert-test`, {}, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/*
 * Export PDF de l'AIPD signée (document organisationnel, aucune donnée
 * personnelle). Retourne un Blob prêt à télécharger.
 */
export async function adminDownloadAipdPdf(adminKey) {
  const res = await axios.get(`${API}/admin/aipd/pdf`, {
    headers: { "X-Admin-Key": adminKey },
    responseType: "blob",
  });
  return res.data;
}

/*
 * Export PDF du guide propriétaire : actions restantes hors code (DNS
 * Brevo, redirection merci, 2FA, DPA) en étapes simples.
 */
export async function adminDownloadGuidePdf(adminKey) {
  const res = await axios.get(`${API}/admin/guide/pdf`, {
    headers: { "X-Admin-Key": adminKey },
    responseType: "blob",
  });
  return res.data;
}

/*
 * Export PDF des statistiques du tunnel : compteurs agrégés et anonymes,
 * prêt à partager. Périodes : 7d, 30d, all.
 */
export async function adminDownloadFunnelPdf(adminKey, period = "all") {
  const res = await axios.get(`${API}/admin/funnel/pdf`, {
    headers: { "X-Admin-Key": adminKey },
    params: { period },
    responseType: "blob",
  });
  return res.data;
}

/*
 * Export CSV des statistiques du tunnel : généré côté serveur, contenu
 * identique au rapport PDF (synthèse, étapes, anti bot). Périodes : 7d, 30d, all.
 */
export async function adminDownloadFunnelCsv(adminKey, period = "all") {
  const res = await axios.get(`${API}/admin/funnel/csv`, {
    headers: { "X-Admin-Key": adminKey },
    params: { period },
    responseType: "blob",
  });
  return res.data;
}

/* Liste des rapports mensuels archivés (métadonnées uniquement). */
export async function adminListMonthlyReports(adminKey) {
  const res = await axios.get(`${API}/admin/monthly-reports`, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/* Télécharge le PDF archivé d'un mois donné (AAAA-MM). */
export async function adminDownloadMonthlyReportPdf(adminKey, monthKey) {
  const res = await axios.get(`${API}/admin/monthly-reports/${monthKey}/pdf`, {
    headers: { "X-Admin-Key": adminKey },
    responseType: "blob",
  });
  return res.data;
}

/* Génère et archive le rapport du mois écoulé sans envoyer d'email. */
export async function adminGenerateMonthlyReport(adminKey) {
  const res = await axios.post(`${API}/admin/monthly-reports/generate`, {}, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/* Nombre de personnes en attente de la notification de retour en ligne. */
export async function adminGetMaintenanceSubscribers(adminKey) {
  const res = await axios.get(`${API}/admin/maintenance/subscribers`, { headers: { "X-Admin-Key": adminKey } });
  return res.data;
}

/*
 * Inscription publique à la notification de retour (page de pause).
 * Usage unique : l'adresse est supprimée dès l'envoi de la notification.
 */
export async function submitMaintenanceNotify(email) {
  const res = await axios.post(`${API}/maintenance/notify`, { email });
  return res.data;
}
